<template>
    <v-form @submit.prevent="addUpdate" ref="form">
        <v-card style="border-radius:4px;">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold">{{mode}} Fringe Benefit</span>
                <v-spacer></v-spacer>
                <help-link appId="compensation" pageId="add-fringe-benefits" color="black"></help-link>
                <v-btn icon @click.native="closeDialog()" >
                    <v-icon >close</v-icon>
                </v-btn>
            </v-toolbar>   
            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                Error encountered. Please contact support. Error details: {{errorMessage}}
            </v-alert>
            <v-card-text>
                <v-layout row wrap> 
                    <v-flex xs12 sm3 class="form-row">       
                        <v-select
                            label="Year"
                            :loading="loading_history"
                            :items="isEditMode?fiscal_years_all:fiscal_years"
                            :rules="[rules.required]" 
                            item-text="fiscalYear"
                            item-value="fiscalPeriodId"
                            :disabled="disabled||isEditMode"
                            v-model="fiscalPeriodId"
                        ></v-select> 
                    </v-flex>
                    <v-flex xs12 sm9 class="form-row">         
                        <v-select
                            label="Benefit Type"
                            :items="benefit_types"
                            :rules="[rules.required]" 
                            item-text="name"
                            item-value="benefitTypeId"
                            :disabled="disabled||mode=='Update'"
                            v-model="benefitTypeId"
                            @change="populateDescription"
                        ></v-select>
                    </v-flex>
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 class="form-row">            
                        <v-alert v-if="description" :value="true" color="info" icon="info" outline dismissible>
                            {{description}}
                        </v-alert>  
                    </v-flex>
                </v-layout> 
                <v-layout row wrap> 
                    <v-flex xs12 sm6 class="form-row"> 
                        <v-text-field label="Monthly Amount" 
                            v-model="monthlyAmount" 
                            :disabled="disabled"
                            :rules="[rules.required_number,rules.max_amount]"
                            type="number" min="1" step="1" value="0.00" 
                            @blur="computeAnnualPayment"
                            ></v-text-field>  
                    </v-flex>
                    <v-flex xs12 sm6 class="form-row">                
                        <v-text-field label="Annual Payment (auto-computed)" 
                            v-model="annualAmount" 
                            :disabled="disabled"
                            readonly
                            hint="Readonly, auto computed"
                            ></v-text-field>  
                    </v-flex>                    
                </v-layout>               
                <v-layout row wrap> 
                    <v-flex xs12 sm8 class="form-row">
                        <v-select
                            v-model="payOnMonth2"
                            :items="futureMonths"
                            label="Payout On"
                            :rules="[rules.required_multiple]"
                            :disabled="disabled"
                            item-text="month"
                            item-value="value"
                            multiple
                            chips
                            @change="computeAnnualPayment"
                            >
                            <!-- SELECT ALL -->
                            <template v-slot:prepend-item>
                                <v-list-tile ripple @click="toggleAllMonths">
                                <v-list-tile-action>
                                    <v-icon :color="payOnMonth2.length ? 'indigo darken-4' : ''">
                                        {{ selectAllIcon }}
                                    </v-icon>
                                </v-list-tile-action>
                                <v-list-tile-content>
                                    <v-list-tile-title>Select All</v-list-tile-title>
                                </v-list-tile-content>
                                </v-list-tile>
                                <v-divider class="mt-2"></v-divider>
                            </template>
                        </v-select>
                    </v-flex>
                    <v-flex v-if="cutoffSettings.type!=1" xs12 sm4 class="form-row">  
                        <v-select
                            label="Payout Schedule"
                            :items="payoutTypes"
                            :rules="[rules.required]" 
                            item-text="description"
                            item-value="payoutMode"
                            :disabled="disabled"
                            v-model="payoutMode"           
                        ></v-select>    
                    </v-flex> 
                </v-layout>
                <v-layout row wrap v-if="isSupMgr">
                    <v-flex xs12 sm12 class="form-row">            
                        <v-switch
                            v-model="nonResident"
                            label="Non-resident alien not engaged in Trade or Business within the Philippines"
                            color="primary"
                            hide-details
                            class="form-switch"
                        ></v-switch>   
                    </v-flex>
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 class="form-row">
                        <v-alert v-if="!isSupMgr" :value="true" color="info" icon="info" outline>
                            <b>Fringe benefits</b> to <b>rank-and-file</b> employees are not taxable with <b>Fringe Benefit Tax</b>, but instead are taxable as compensation income subject to normal income tax rate in Section 24(A) of the NIRC.
                        </v-alert>  
                        <v-alert v-if="isSupMgr" :value="true" color="info" icon="info" outline>
                            <b>Fringe benefits</b> to <b>managerial</b> or <b>supervisory</b> level employees are subject to <b>Fringe Benefit Tax</b>. Benefits subjected to <b>Fringe Benefit Tax</b> are no longer included in the employee's taxable income.
                        </v-alert> 
                    </v-flex>
                </v-layout> 
                 <v-layout row wrap v-if="showComment"> 
                    <v-flex xs12 sm12 class="form-comment"> 
                        <v-textarea v-model="remarks" label="Comments" rows="2" color="primary"></v-textarea>
                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn icon @click="showComment=!showComment" title="Add Comment">
                    <v-icon>comment</v-icon>
                </v-btn>
                <v-btn color="primary" type="submit" :disabled="disabled" :loading="loading" dark>{{mode}}</v-btn>
                <v-btn v-if="isEditMode" @click="clearForm" :disabled="disabled">clear</v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>

import benefitsApi from '@/api/benefits'
import compAllocApi from '@/api/compallocation'
import payoutTypes from '@/constants/payout-types'

export default {
    data () {
        return {
            payoutTypes,
            loading: false,
            loading_history: false,
            disabled: false,
            error: false,
            errorMessage: null,
            rules: {
                required: value => !!value || 'Required field',
                required_number: value => !!value || 'Required field. Number only.',
                required_multiple: value => value.length>0 || 'Must select at least one month.',
                max_amount: value => value<=1000000 || 'Invalid Amount. Maximum of 1,000,000.00' 
            },
            months: [
                {month: "January", value: 1},
                {month: "February", value: 2},
                {month: "March", value: 3},
                {month: "April", value: 4},
                {month: "May", value: 5},
                {month: "June", value: 6},
                {month: "July", value: 7},
                {month: "August", value: 8},
                {month: "September", value: 9},
                {month: "October", value: 10},
                {month: "November", value: 11},
                {month: "December", value: 12}
            ],    
            mode: null,
            showComment: false,
            fringeBenefitsId: null,
            selectedUserId: null,
            fiscalPeriodId: null,
            benefitTypeId: null,
            monthlyAmount: null,
            payOnMonth: null,
            payOnMonth2: [],
            nonResident: false,
            recurringAnnually: false,
            taxable: null,
            remarks: '',
            payGrade: null,
            payoutMode: null,
            benefit_types: [],
            description: null,
            type: 'De Minimis',
            annualAmount: null,
            salaryHistory: []
        }
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
    }, 
    methods: {
        async setAdd(data) {
            this.mode = "Add"
            
            if (!this.benefit_types.length) 
                await this.getBenefitTypes()

            this.selectedUserId = data.userId  

            if (!this.salaryHistory.length)
                await this.getSalaryHistory()

            this.clearForm()
        },
        async setUpdate(data) {
            this.mode = "Update"

            if (!this.benefit_types.length) 
                await this.getBenefitTypes()

            this.selectedUserId = data.userId
            this.fiscalPeriodId = data.fringebenefits.fiscalPeriodId
            this.fringeBenefitsId = data.fringebenefits.fringeBenefitsId           
            this.benefitTypeId = data.fringebenefits.benefitTypeId
            this.monthlyAmount = data.fringebenefits.monthlyAmount
            this.payOnMonth = data.fringebenefits.payOnMonth
            this.payOnMonth2 = data.fringebenefits.payOnMonth2
            this.payoutMode = data.fringebenefits.payoutMode
            this.nonResident = data.fringebenefits.nonResident
            this.remarks = ''
            this.payGrade = data.payGrade

            if (!this.salaryHistory.length)
                await this.getSalaryHistory()

            this.computeAnnualPayment()
            this.populateDescription()
        },
        async addUpdate() {
            try {
                this.loading = true
                this.disabled = true

                if (this.$refs.form.validate()) {

                    var data = {
                        fringeBenefitsId: this.fringeBenefitsId,
                        fiscalPeriodId: this.fiscalPeriodId,
                        userId: this.selectedUserId,
                        benefitTypeId: this.benefitTypeId,                      
                        monthlyAmount: this.monthlyAmount,
                        payOnMonth: this.payOnMonth,
                        payOnMonth2: this.payOnMonth2.sort((a,b) => a-b),
                        nonResident: this.nonResident,
                        
                        //if 2 cutoffs per month: 1-payout on first cutoff, 2 payout on 2nd cutoff, 3 divide amount to 2 cutoffs
	                    //if 1 cutoff per month: 4-payout all in one cutoff
                        payoutMode: this.isBiMonthlyCutoff ? this.payoutMode : 4,
                        recurringAnnually: this.recurringAnnually,
                        remarks: this.remarks,

                        //used for update history only, not saved in table
                        benefitName: this.selectedBenefitName,
                        fiscalYear: this.selectedFiscalYear
                    }

                    if (this.mode == 'Add') {
                        const response = await benefitsApi.addFringeBenefits(data)
                        if (response.status == 200) {
                            this.$emit('send', '')
                        }
                    }else{
                        const response = await benefitsApi.updateFringeBenefits(data)
                        if (response.status == 200) {
                            this.$emit('send', '')
                        }
                    }
                    this.closeDialog()
                }

                this.loading = false
                this.disabled = false
            }
            catch (error) {
                this.loading = false
                this.disabled = false
                this.error = true
                this.errorMessage = error
            }
        },
        toggleAllMonths() {
            this.$nextTick(() => {
                if (this.allMonthsSelected) {
                    this.payOnMonth2 = []
                } else {
                    this.payOnMonth2 = this.allMonthValues.slice()
                }
                this.computeAnnualPayment()
            })
        },
        computeAnnualPayment() {
            const multiplier = this.payOnMonth2.length ? this.payOnMonth2.length : 1
            this.annualAmount = 0
            if (this.monthlyAmount > 0) {
                this.annualAmount = this.monthlyAmount * multiplier
            }
        },
        populateDescription() {
            var data = this.benefit_types.filter(item => {
                return item.benefitTypeId == this.benefitTypeId
            })
            this.description = data[0].description
        },
        async getBenefitTypes() {
            try {
                const response = await benefitsApi.getBenefitTypes()
                if (response && response.status == 200) {
                    this.benefit_types = response.data.items.filter(item => {
                        return item.type == 'Fringe'
                    })
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async getSalaryHistory() {
            try {
                this.loading_history = true
                const response = await compAllocApi.getSalaryHistory(this.selectedUserId)
                if (response && response.status == 200) {
                    this.salaryHistory = response.data.items
                }
                this.loading_history = false
            }catch(error) {
                this.error = true
                this.errorMessage = error
            }
        },
        closeDialog() {
            this.$emit('update:dialogAddEditFringe', false)
        },
        clearForm() {
            this.$refs.form.reset()
            this.remarks = ''
            this.loading = false
            this.error = false
            this.errorMessage = ''
            this.description = null
        },
    },
    computed: {
        cutoffSettings() {
            return this.$store.state.cutOffSettings
        },
        isEditMode() {
            return this.mode == 'Update'
        },
        isBiMonthlyCutoff() {
            if (this.cutoffSettings) {
                return (this.cutoffSettings.second_From && this.cutoffSettings.second_To)
            }
        },
        isSupMgr() {
            const salary = this.salaryHistory.find(x => x.fiscalYear == this.selectedFiscalYear)
            if (salary) 
                return salary.payGrade.includes('Supervisory') || salary.payGrade.includes('Managerial')
            return false          
        },
        futureMonths() {
            const currentMonth = new Date().getMonth() + 1; 
            this.months.forEach(item => {
                //item.disabled = item.value < currentMonth
                item.disabled  = false //always enable all months
            })
            return this.months
        },
        fiscal_years() {
            const currentYear = new Date().getFullYear()
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods.filter(x => x.fiscalYear >= currentYear)
            }
            return [{fiscalYear: currentYear}]
        },
        fiscal_years_all() {
            const currentYear = new Date().getFullYear()
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods
            }
            return [{fiscalYear: currentYear}]
        },
        allMonthValues() {
            return this.futureMonths.map(m => m.value)
        },
        allMonthsSelected() {
            return this.payOnMonth2.length === this.allMonthValues.length
        },
        someMonthsSelected() {
            return this.payOnMonth2.length > 0 && !this.allMonthsSelected
        },
        selectAllIcon() {
            if (this.allMonthsSelected) return 'check_box'
            if (this.someMonthsSelected) return 'indeterminate_check_box'
            return 'check_box_outline_blank'
        },
        selectedBenefitName() {
            if (this.benefitTypeId) 
                return this.benefit_types.find(x => x.benefitTypeId == this.benefitTypeId).name
        },
        selectedFiscalYear() {
            if (this.fiscalPeriodId)
                return this.fiscal_years_all.find(x => x.fiscalPeriodId == this.fiscalPeriodId).fiscalYear
        },
    },
    created() {}
}
</script>
<style scoped>
.form-row {
    padding: 0px 15px 0px 15px;
}
.form-comment {
    margin:5px 15px -25px 15px;
}
.form-switch {
    margin:0px 0px 10px 5px;
}
</style>